import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchTaskMemberInvitesCacheKey,
  FetchFinTaskMemberInvitesFilters,
  FetchTaskMemberInvitesSort,
  FetchTaskMemberInvitesPage,
  FetchTaskMemberInvitesLimit,
  FetchTaskMemberInvitesGqlQuery
} from '../../taskMemberInvitesTypes';
import { FetchCacheTaskMemberInvitesQueryResponse } from '../../queries/fetchCacheTaskMemberInvites.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_TASK_MEMBER_INVITES_FILTERS,
  INITIAL_TASK_MEMBER_INVITES_LIMIT,
  INITIAL_TASK_MEMBER_INVITES_PAGE,
  INITIAL_TASK_MEMBER_INVITES_SORT
} from '../../taskMemberInvitesConstants';

interface TaskMemberInvitesWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface TaskMemberInvitesWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface TaskMemberInvitesDefaultOptions {
  action?: string;
  cacheKey: FetchTaskMemberInvitesCacheKey;
  query: FetchTaskMemberInvitesGqlQuery;
  initialFilters?: FetchFinTaskMemberInvitesFilters;
  initialSort?: FetchTaskMemberInvitesSort;
  initialPage?: FetchTaskMemberInvitesPage;
  initialLimit?: FetchTaskMemberInvitesLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type TaskMemberInvitesOptions = TaskMemberInvitesDefaultOptions &
  (
    | TaskMemberInvitesWithPrefetchItemOptions
    | TaskMemberInvitesWithoutPrefetchItemOptions
  );

const scope = 'taskMemberInvites';

function useCachePaginatedTaskMemberInvites<
  FetchTaskMemberInvitesType = FetchCacheTaskMemberInvitesQueryResponse
>({
  action,
  cacheKey,
  initialFilters = INITIAL_TASK_MEMBER_INVITES_FILTERS,
  initialSort = INITIAL_TASK_MEMBER_INVITES_SORT,
  initialPage = INITIAL_TASK_MEMBER_INVITES_PAGE,
  initialLimit = INITIAL_TASK_MEMBER_INVITES_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: TaskMemberInvitesOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchTaskMemberInvitesType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    taskMemberInvitesData: data,
    taskMemberInvites: items,
    taskMemberInvitesError: itemsError,
    taskMemberInvitesErrorMessage: itemsErrorMessage,
    taskMemberInvitesTotalCount: itemsTotalCount,
    taskMemberInvitesFetched: isFetched,
    taskMemberInvitesLoading: isLoading,
    taskMemberInvitesIsPlaceholderData: isPlaceholderData,
    taskMemberInvitesFilters: currentFilters,
    taskMemberInvitesSort: currentSort,
    taskMemberInvitesPage: currentPage,
    taskMemberInvitesLimit: currentLimit,
    updateTaskMemberInviteCache: updateItemCache,
    updateTaskMemberInvitesCache: updateItemsCache,
    filterTaskMemberInvites: filterItems,
    changeTaskMemberInvitesFilters: changeItemsFilters,
    clearTaskMemberInvitesFilters: clearItemsFilters,
    sortTaskMemberInvites: sortItems,
    paginateTaskMemberInvites: paginateItems,
    limitTaskMemberInvites: limitItems,
    prefetchTaskMemberInvites: prefetchItems,
    prefetchTaskMemberInvite: prefetchItem,
    taskMemberInvitesPaginationInfo: data?.[scope]?.paginationInfo
  };
}

export default useCachePaginatedTaskMemberInvites;
